import { DownloadTwoTone, FilterAltTwoTone } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  guideTourAccommodationGroupList,
  guideTourAccommodationGroupSettledListPrint,
} from '../api';
import { useAuth } from '../hooks/useAuth';
import { download } from '../utils';
import { IGuideTourAccommodationGroupSettledList } from '../utils/types';
import GeneralCustomerListFilterDialog from './GeneralCustomerListFilterDialog';
import GuideTourAccommodationRoomGroupTable from './GuideTourAccommodationRoomGroupTable';
type GuideTourAccommodationRoomGroupProps = {
  salesIndividualId: string;
  cityName: string;
  tourPlanId: number;
  open: boolean;
  onClose: () => void;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

export default function GuideTourAccommodationRoomGroupDialog({
  tourPlanId,
  cityName,
  salesIndividualId,
  open,
  onClose,
}: GuideTourAccommodationRoomGroupProps) {
  const [accommodationGroupSettledList, setAccommodationGroupSettledList] =
    useState<IGuideTourAccommodationGroupSettledList[]>([]);
  const [filteredData, setFilteredData] = useState<
    IGuideTourAccommodationGroupSettledList[]
  >([]);
  const { token } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSalesGroups, setSelectedSalesGroups] = useState<string[]>([]);
  const [openFilter, setOpenFilter] = useState(false);

  useEffect(() => {
    filterData();
  }, [searchQuery, selectedSalesGroups]);

  useEffect(() => {
    if (selectedSalesGroups.length === 0) {
      setFilteredData(accommodationGroupSettledList);
    }
  }, [selectedSalesGroups, accommodationGroupSettledList]);

  useEffect(() => {
    fetchGuideTourAccommodationGroupSettledList(salesIndividualId, tourPlanId);
  }, [salesIndividualId, tourPlanId, selectedSalesGroups]);

  const salesGroupOptions = Array.from(
    new Set(accommodationGroupSettledList.map((item) => item.salesOrderGroup)),
  ).map((salesOrderGroup) => salesOrderGroup);

  const filterData = () => {
    if (!searchQuery && selectedSalesGroups.length === 0) {
      setFilteredData(accommodationGroupSettledList);
    } else {
      const searchedPersons = accommodationGroupSettledList.filter((item) =>
        item.customerFullName.toLowerCase().includes(searchQuery.toLowerCase()),
      );

      let filtered = [...accommodationGroupSettledList];

      if (selectedSalesGroups.length > 0) {
        filtered = filtered.filter((item) =>
          selectedSalesGroups.includes(item.salesOrderOffice),
        );
      }
      if (searchQuery) {
        filtered = filtered.filter((item) =>
          item.customerFullName
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
        );
      }

      if (searchedPersons.length > 0) {
        filtered = filtered.filter((item) =>
          searchedPersons.some(
            (searchedPerson) => item.roomGroupId === searchedPerson.roomGroupId,
          ),
        );
      }

      setFilteredData(filtered);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handlePrint = () => {
    fetchGuideTourAccommodationGroupSettledListPrint(
      salesIndividualId,
      tourPlanId,
    );
  };

  const fetchGuideTourAccommodationGroupSettledList = async (
    salesIndividualId: string,
    tourPlanId: number,
  ) => {
    try {
      const res = await guideTourAccommodationGroupList(
        salesIndividualId,
        tourPlanId,
        token,
      );
      const data = await res.json();
      if (res?.status === 200) {
        setAccommodationGroupSettledList(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const fetchGuideTourAccommodationGroupSettledListPrint = async (
    salesIndividualId: string,
    tourPlanId: number,
  ) => {
    try {
      const res = await guideTourAccommodationGroupSettledListPrint(
        salesIndividualId,
        tourPlanId,
        token,
      );
      const objectURL = window.URL.createObjectURL(await res.blob());
      const fileName =
        cityName + ' ' + t('guideGeneralCustomerList.customerList');
      download(objectURL, fileName);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    }
  };

  return (
    <Dialog
      sx={{ opacity: 1 }}
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <DialogTitle sx={{ display: 'flex' }}>
        {t('guideTourAccommodationInfo.title', { city: cityName })}
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress color="secondary" />
            <Typography sx={{ fontSize: 18, mt: 3 }}>
              {t('balancesQueryPage.loading')}
            </Typography>
          </Box>
        )}
        {!loading && (
          <Box>
            <Box mt={1} mb={3}>
              <Box display="flex" alignItems="center">
                <Box sx={{ flex: 1 }} />
                <IconButton onClick={handlePrint} sx={{ mr: 1 }}>
                  <DownloadTwoTone />
                </IconButton>

                <IconButton
                  onClick={() => setOpenFilter(!openFilter)}
                  sx={{ mr: 1 }}
                >
                  <FilterAltTwoTone />
                </IconButton>

                <TextField
                  label={t('guideTourAccommodationInfo.searchPerson')}
                  placeholder={t('guideTourAccommodationInfo.searchWithName')}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  sx={{ mr: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <GeneralCustomerListFilterDialog
                open={openFilter}
                onClose={() => setOpenFilter(false)}
                salesGroupOptions={salesGroupOptions}
                selectedSalesGroups={selectedSalesGroups}
                setSelectedSalesGroups={setSelectedSalesGroups}
              />

              <GuideTourAccommodationRoomGroupTable
                accommodationGroupSettledList={filteredData}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
